import * as React from 'react';
import { OrderPackageFormFieldsFragment } from 'types/generated';
import { useEffect } from 'react';
import { PAKKAFORM_ID } from '@/config/constants';
import { useAtom, useSetAtom } from 'jotai';
import { addOnsAtom, addOnsQuantityAtom, clearPackageAtom, setPackageAtom, isPackageFormOpenAtom } from '@/store';
import { getPackageDescription, getPackageQuantity } from './PackageFormUtils';
import PackageCart from './PackageCart';
import PackageForm from './PackageForm';

type TraitsListProps = {
  data: OrderPackageFormFieldsFragment;
};

const OrderPackageForm = ({ data }: TraitsListProps) => {
  const [addOns] = useAtom(addOnsAtom);
  const setPackage = useSetAtom(setPackageAtom);
  const clearPackage = useSetAtom(clearPackageAtom);
  const setAddOnsQuantity = useSetAtom(addOnsQuantityAtom);
  const [isFormVisible, setIsFormVisible] = useAtom(isPackageFormOpenAtom);

  const [multiAddOns, setMultiAddOns] = React.useState(Object.values(addOns).filter(({ addOn }) => addOn.multiselect));
  const [singleAddOns, setSingleAddOns] = React.useState(
    Object.values(addOns).filter(({ addOn }) => !addOn.multiselect)
  );
  const [hasAddons, setHasAddons] = React.useState<boolean>(Object.values(addOns).length > 0);
  const packageDescription = React.useMemo(() => getPackageDescription(addOns, data.title || ''), [data.title, addOns]);
  const packageQuantity = React.useMemo(() => getPackageQuantity(addOns), [addOns]);

  const [isCartOpen, setIsCartOpen] = React.useState<boolean>(false);
  const [formStep, setFormStep] = React.useState<number>(1);
  const [loadingAddons, setLoadingAddOns] = React.useState(true);

  const willUnmountRef = React.useRef(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddOnsQuantity({
      id: event.target.id,
      change: event.target.checked ? 1 : -1,
    });
  };

  useEffect(() => {
    if (data.packagePrice && data.id) {
      setPackage({ id: data.id, basePrice: data.packagePrice, title: data.title || '' });
    }
  }, [data.packagePrice, data.id, data.title, setPackage]);

  useEffect(() => {
    return () => {
      if (willUnmountRef.current) {
        clearPackage();
        setIsFormVisible(false);
        setIsCartOpen(false);
        setFormStep(1);
      }
    };
  }, [clearPackage, setIsFormVisible]);

  useEffect(() => {
    return () => {
      willUnmountRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (addOns) {
      setMultiAddOns(Object.values(addOns).filter(({ addOn }) => addOn.multiselect));
      setSingleAddOns(Object.values(addOns).filter(({ addOn }) => !addOn.multiselect));
      setHasAddons(Object.values(addOns).length > 0);
    }
    setLoadingAddOns(false);
  }, [addOns]);

  useEffect(() => {
    if (isFormVisible) {
      setIsCartOpen(false);
    }
  }, [isFormVisible]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const chatContainer = document.getElementById('chat-launcher-container');
      if (chatContainer) {
        chatContainer.classList.add('hidden');
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      const chatContainer = document.getElementById('chat-launcher-container');
      if (chatContainer) {
        chatContainer.classList.remove('hidden');
      }
      observer.disconnect();
    }; // Clean up
  }, []);

  if (loadingAddons) {
    return <></>;
  }

  return (
    <>
      <PackageCart
        data={data}
        addOns={addOns}
        hasAddons={hasAddons}
        singleAddOns={singleAddOns}
        multiAddOns={multiAddOns}
        isCartOpen={isCartOpen}
        isFormVisible={isFormVisible}
        packageQuantity={packageQuantity}
        setIsCartOpen={setIsCartOpen}
        handleCheckboxChange={handleCheckboxChange}
      />

      <div id={PAKKAFORM_ID}>
        <PackageForm
          data={data}
          addOns={addOns}
          hasAddons={hasAddons}
          singleAddOns={singleAddOns}
          multiAddOns={multiAddOns}
          packageQuantity={packageQuantity}
          formStep={formStep}
          packageDescription={packageDescription}
          handleCheckboxChange={handleCheckboxChange}
          setIsCartOpen={setIsCartOpen}
          setFormStep={setFormStep}
        />
      </div>
    </>
  );
};

export default OrderPackageForm;
